/**
 * API 客户端配置
 * 基于 Axios 封装的 HTTP 请求客户端
 */
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'sonner';

// 创建 Axios 实例
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const MOBILE_API_TIMEOUT_MS = 15_000;
const AUTH_REFRESH_TIMEOUT_MS = 10_000;

// 安全获取并解析本地存储的认证状态
const getPersistedAuth = () => {
  try {
    const authStorage = localStorage.getItem('auth-storage');
    return authStorage ? JSON.parse(authStorage) : null;
  } catch (e) {
    console.error('[apiClient] Failed to parse auth-storage:', e);
    return null;
  }
};

const normalizeErrorMessage = (message: unknown, fallback = '请求失败'): string => {
  if (!message) return fallback;
  if (typeof message === 'string') return message;
  if (message instanceof Error) return message.message || fallback;
  if (Array.isArray(message)) {
    return message
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          const record = item as Record<string, unknown>;
          const loc = Array.isArray(record.loc) ? record.loc.join('.') : '';
          const msg = typeof record.msg === 'string' ? record.msg : '';
          return [loc, msg].filter(Boolean).join(': ');
        }
        return String(item);
      })
      .filter(Boolean)
      .join('；') || fallback;
  }
  if (typeof message === 'object') {
    try {
      return JSON.stringify(message);
    } catch {
      return fallback;
    }
  }
  return String(message);
};

const shouldSuppressErrorToast = (config?: InternalAxiosRequestConfig | AxiosError['config'] | null): boolean => {
  return Boolean((config as any)?.suppressErrorToast);
};

const isPublicAuthRoute = (url?: string): boolean => {
  if (!url) return false;
  return [
    '/auth/login',
    '/auth/refresh',
    '/auth/logout',
    '/auth/wecom/qr',
    '/auth/wecom/redirect',
    '/auth/wecom/callback',
    '/auth/wecom/js-config-telemetry',
  ].some((route) => url.includes(route));
};

// 请求拦截器：添加 JWT Token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 手机端页面必须在可预期时间内结束加载状态。即使某个业务接口发生慢查询，
    // 也不能让企微 WebView 一直停留在转圈页；显式更短的超时仍会被保留。
    if (
      typeof window !== 'undefined'
      && window.location.pathname.startsWith('/m')
      && (!config.timeout || config.timeout > MOBILE_API_TIMEOUT_MS)
    ) {
      config.timeout = MOBILE_API_TIMEOUT_MS;
    }

    // 优先从内存中的全局 token 读取，防止 localStorage 写入异步导致的 401 Race Condition
    let token = null;
    if (typeof window !== 'undefined' && (window as any).__wecom_token__) {
      token = (window as any).__wecom_token__;
    }
    if (!token) {
      const parsedAuth = getPersistedAuth();
      token = parsedAuth?.state?.token;
    }

    // 如果是登录或授权等公开接口，绝对不带 Bearer Token，避免因旧 Token 过期导致后端拦截返回 401
    const isAuthRoute = isPublicAuthRoute(config.url);

    if (token && config.headers && !isAuthRoute) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// 是否正在刷新 Token
let isRefreshing = false;
// 等待 Token 刷新的请求队列
let pendingRequests: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

// 处理等待中的请求
const processPendingRequests = (token: string | null, error: unknown = null) => {
  pendingRequests.forEach(({ resolve, reject }) => {
    if (token) {
      resolve(token);
    } else {
      reject(error);
    }
  });
  pendingRequests = [];
};

// 响应拦截器：统一错误处理
apiClient.interceptors.response.use(
  (response) => {
    // 如果返回的是标准 API 响应格式，直接返回 data 字段
    const { data } = response;
    if (data && typeof data === 'object' && 'code' in data) {
      if (data.code === 0 || data.code === 200) {
        return data;
      }
      // 业务错误
      const errorMessage = normalizeErrorMessage(data.message, '请求失败');
      if (!shouldSuppressErrorToast(response.config)) {
        toast.error(errorMessage);
      }
      return Promise.reject(new Error(errorMessage));
    }
    return data;
  },
  async (error: AxiosError<{ message?: string; detail?: string; code?: number }>) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }
    const { response, config } = error;

    if (!response) {
      if (!shouldSuppressErrorToast(config)) {
        toast.error('网络错误，请检查网络连接');
      }
      return Promise.reject(error);
    }

    const { status, data } = response;

    // 401 未授权 - 尝试刷新 Token
    if (status === 401) {
      // 登录和认证相关接口，若触发 401 直接拒绝，防止死循环和挂起
      const isAuthRoute = isPublicAuthRoute(config?.url);
      if (isAuthRoute) {
        return Promise.reject(error);
      }

      let refreshToken = null;
      if (typeof window !== 'undefined' && (window as any).__wecom_refresh_token__) {
        refreshToken = (window as any).__wecom_refresh_token__;
      }
      if (!refreshToken) {
        const parsedAuth = getPersistedAuth();
        refreshToken = parsedAuth?.state?.refreshToken;
      }

      if (!refreshToken) {
        // 没有 refreshToken，直接跳转登录
        localStorage.removeItem('auth-storage');
        if (typeof window !== 'undefined') {
          (window as any).__wecom_token__ = null;
          (window as any).__wecom_refresh_token__ = null;
        }
        window.location.href = '/login';
        return Promise.reject(error);
      }

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const res = await axios.post(
            `${apiClient.defaults.baseURL}/auth/refresh`,
            { refresh_token: refreshToken },
            { timeout: AUTH_REFRESH_TIMEOUT_MS },
          );
          const newToken = res.data?.data?.access_token || res.data?.access_token || res.data?.data?.token || res.data?.token;
          const newRefreshToken = res.data?.data?.refresh_token || res.data?.refresh_token;

          if (newToken) {
            // 更新 zustand persist 存储
            const parsed = getPersistedAuth() || { state: {} };
            parsed.state = {
              ...parsed.state,
              token: newToken,
              ...(newRefreshToken ? { refreshToken: newRefreshToken } : {}),
            };
            localStorage.setItem('auth-storage', JSON.stringify(parsed));

            // 同步更新 window 全局变量，彻底解决 401 刷新无限循环死锁问题！
            if (typeof window !== 'undefined') {
              (window as any).__wecom_token__ = newToken;
              if (newRefreshToken) {
                (window as any).__wecom_refresh_token__ = newRefreshToken;
              }
            }

            // 动态引入 useAuthStore 并更新 Zustand 状态，确保 UI 和生命周期状态一致
            try {
              const { useAuthStore } = await import('../stores/authStore');
              useAuthStore.setState({
                token: newToken,
                ...(newRefreshToken ? { refreshToken: newRefreshToken } : {}),
              });
            } catch (err) {
              console.error('[apiClient] Failed to update Zustand store:', err);
            }

            processPendingRequests(newToken);
            // 重试原始请求
            if (config?.headers) {
              config.headers.Authorization = `Bearer ${newToken}`;
            }
            return apiClient(config!);
          } else {
            throw new Error('Refresh token API returned no access_token');
          }
        } catch (refreshError) {
          processPendingRequests(null, refreshError);
          localStorage.removeItem('auth-storage');
          if (typeof window !== 'undefined') {
            (window as any).__wecom_token__ = null;
            (window as any).__wecom_refresh_token__ = null;
          }
          window.location.href = '/login';
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // 正在刷新 Token，将请求加入队列
      return new Promise((resolve, reject) => {
        pendingRequests.push({
          resolve: (token: string) => {
            if (config?.headers) {
              config.headers.Authorization = `Bearer ${token}`;
            }
            resolve(apiClient(config!));
          },
          reject,
        });
      });
    }

    // 403 禁止访问
    if (status === 403) {
      if (!shouldSuppressErrorToast(config)) {
        toast.error('您没有权限执行此操作');
      }
      return Promise.reject(error);
    }

    // 404 未找到
    if (status === 404) {
      if (!shouldSuppressErrorToast(config)) {
        toast.error('请求的资源不存在');
      }
      return Promise.reject(error);
    }

    // 500 服务器错误
    if (status >= 500) {
      if (!shouldSuppressErrorToast(config)) {
        toast.error('服务器错误，请稍后重试');
      }
      return Promise.reject(error);
    }

    // 410 已过期 - 不弹窗提示，交由业务组件自身渲染过期状态
    if (status === 410) {
      return Promise.reject(error);
    }

    // 其他错误
    const errorMessage = normalizeErrorMessage(data?.message || data?.detail, '请求失败');
    if (!shouldSuppressErrorToast(config)) {
      toast.error(errorMessage);
    }
    return Promise.reject(new Error(errorMessage));
  }
);

export { apiClient };
